import React, { useState, useEffect } from 'react';
import { Container, Spinner, Modal, Form, Row, Col, Button, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getAllMenus, updateMenu, updateSubmenu, updateSubmenuSequence } from '../../services/menuMaster-service';
import InputBox from '../common/InputBox';
import SearchSelect from '../common/SearchSelect';
import '../admin/DragDrop.css';

const MenuMaster = () => {
  const [menus, setMenus] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [editType, setEditType] = useState(''); // 'menu' or 'submenu'
  const [formData, setFormData] = useState({
    menuName: '',
    submenu: '',
    status: 'Active',
    menuURL: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const [submit, setSubmit] = useState(false);
  const [fieldCount, setFieldCount] = useState([]);
  const [mandatoryFields, setMandatoryFields] = useState([]);
  const [reload, setReload] = useState(0);
  const [writeAccess, setWriteAccess] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    if (reload > 0) {
      loadMenus();
    }
  }, [reload]);

  useEffect(() => {
    loadMenus();
    checkWriteAccess();
  }, []);

  const checkWriteAccess = () => { // NOSONAR
    let AuthorizedSessionstmp = sessionStorage.getItem("AuthorizedSessions");
    if (!AuthorizedSessionstmp || AuthorizedSessionstmp === "") {
      setWriteAccess(false);
      return;
    }
    try {
      let AuthorizedSessions = JSON.parse(AuthorizedSessionstmp);
      let hasAccess = false;
      for (let parentMenu in AuthorizedSessions) {
        if (AuthorizedSessions[parentMenu]) {
          for (let menu of AuthorizedSessions[parentMenu]) {
            if (menu.submenu === "Menu Master" && menu.write === true) {
              hasAccess = true;
              break;
            }
          }
        }
        if (hasAccess) break;
      }
      setWriteAccess(hasAccess);
    } catch (error) {
      console.error('Error parsing AuthorizedSessions:', error);
      setWriteAccess(false);
    }
  };

  const loadMenus = async () => {
    setLoading(true);
    try {
      const response = await getAllMenus();
      let menuData = response.data || [];
      
      // Fix sequence1 for menus that have 0 but submenus with correct sequence1
      menuData = menuData.map(menu => {
        if ((menu.sequence1 === 0 || !menu.sequence1) && menu.submenus && menu.submenus.length > 0) {
          return {
            ...menu,
            sequence1: menu.submenus[0].sequence1 || 0
          };
        }
        return menu;
      });
      
      // Sort by sequence1
      menuData.sort((a, b) => (parseInt(a.sequence1) || 0) - (parseInt(b.sequence1) || 0));
      
      setMenus(menuData);
    } catch (error) {
      console.error('Error loading menus:', error);
      toast.error('Failed to load menus');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMenu = (menu) => {
    setEditingItem(menu);
    setEditType('menu');
    setFormData({
      menuName: menu.menuName,
      submenu: '',
      status: menu.status,
      menuURL: menu.menuURL
    });
    setShowModal(true);
  };

  const handleEditSubmenu = (menu, submenu) => {
    setEditingItem({ ...submenu, menuName: menu.menuName });
    setEditType('submenu');
    setFormData({
      menuName: menu.menuName,
      submenu: submenu.submenu,
      status: submenu.status,
      menuURL: submenu.menuURL
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setEditType('');
    setFormData({
      menuName: '',
      submenu: '',
      status: 'Active',
      menuURL: ''
    });
    setError({});
    setMandatoryFields([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitForm = (e) => {
    e.preventDefault();
    setSubmit(submit ? false : true);
  };

  useEffect(() => {
    const fieldValidation = editType === 'menu' ? 2 : 3;
    if (fieldCount.length >= fieldValidation) {
      setSubmit(submit ? false : true);
      handleSubmit();
      setFieldCount([]);
    }
  }, [fieldCount]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const currentUser = JSON.parse(sessionStorage.getItem('userData') || '{}');
      const submitData = {
        ...formData,
        modifiedBy: currentUser.userId || 'system'
      };

      let response;
      if (editType === 'menu') {
        response = await updateMenu(submitData);
      } else {
        response = await updateSubmenu(submitData);
      }

      toast.success(response.msg);
      handleCloseModal();
      setReload(prev => prev + 1);
    } catch (error) {
      console.error('Error saving:', error);
      const errorMsg = typeof error === 'string' ? error : error?.msg || error?.statusMsg || 'Failed to save';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmenuDragStart = (e, submenu, index, menuName) => {
    setDraggedItem({ item: submenu, index, type: 'submenu', menuName });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleSubmenuDrop = async (e, dropIndex, menuName) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== 'submenu' || draggedItem.menuName !== menuName || draggedItem.index === dropIndex) {
      handleDragEnd();
      return;
    }

    const menu = menus.find(m => m.menuName === menuName);
    const newSubmenus = [...menu.submenus];
    const [draggedSubmenu] = newSubmenus.splice(draggedItem.index, 1);
    newSubmenus.splice(dropIndex, 0, draggedSubmenu);

    const sequenceUpdates = newSubmenus.map((submenu, index) => ({
      menuName: menuName,
      submenu: submenu.submenu,
      sequence1: submenu.sequence1, // Keep existing sequence1
      sequence2: index + 1 // Update only sequence2
    }));

    try {
      setLoading(true);
      await updateSubmenuSequence(sequenceUpdates);
      toast.success('Submenu sequence updated successfully!');
      
      // Trigger menu update event for sidebar refresh
      console.log('Dispatching menuUpdated event');
      window.dispatchEvent(new CustomEvent('menuUpdated'));
      
      setReload(prev => prev + 1);
    } catch (error) {
      console.error('Error updating submenu sequence:', error);
      toast.error('Failed to update submenu sequence');
    } finally {
      setLoading(false);
      handleDragEnd();
    }
  };

  const toggleMenuExpansion = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  return (
    <>
      {loading && (
        <div className="spinner-box">
          <Spinner animation="border" className="spinner" />
        </div>
      )}

      <Container bsPrefix="main-body">
        <div className="page-title">Menu Management</div>
        <div className="page-sub-title">Manage Menu Master Data</div>
        
        <div className="menu-master-container">
          <Table className="menu-master-table" striped bordered hover>
            <thead>
              <tr>
                <th width="50%">Menu / Submenu</th>
                <th width="15%">Sequence</th>
                <th width="15%">Status</th>
                <th width="15%">Menu URL</th>
                <th width="5%">Actions</th>
              </tr>
            </thead>
            <tbody>
              {menus.map((menu, menuIndex) => (
                <React.Fragment key={menu.menuName}>
                  <tr className={`menu-row`}>
                    <td>
                      <div className="menu-name-container">
                        <span 
                          className="menu-expand-icon"
                          onClick={() => toggleMenuExpansion(menu.menuName)}
                        >
                          {expandedMenus[menu.menuName] ? '▼' : '▶'}
                        </span>
                        <strong>{menu.menuName}</strong>
                      </div>
                    </td>
                    <td></td>
                    <td><span className={`status-badge ${menu.status.toLowerCase()}`}>{menu.status}</span></td>
                    <td>{menu.menuURL}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className={`add-btn table-action-btn ${!writeAccess ? "disabled" : ""}`}
                          onClick={() => writeAccess ? handleEditMenu(menu) : null}
                          disabled={!writeAccess}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedMenus[menu.menuName] && menu.submenus.map((submenu, submenuIndex) => (
                    <tr
                      key={`${menu.menuName}-${submenu.submenu}`}
                      className={`submenu-row ${draggedItem && draggedItem.type === 'submenu' && draggedItem.menuName === menu.menuName && draggedItem.index === submenuIndex ? 'dragging' : ''} ${dragOverIndex === submenuIndex && draggedItem?.type === 'submenu' ? 'drag-over' : ''}`}
                      draggable={true}
                      onDragStart={(e) => handleSubmenuDragStart(e, submenu, submenuIndex, menu.menuName)}
                      onDragOver={(e) => handleDragOver(e, submenuIndex)}
                      onDragEnd={handleDragEnd}
                      onDrop={(e) => handleSubmenuDrop(e, submenuIndex, menu.menuName)}
                    >
                      <td>
                        <div className="submenu-name-container">
                          <span className="submenu-indent">└─</span>
                          {submenu.submenu}
                        </div>
                      </td>
                      <td><span className="sequence-badge">{submenu.sequence2}</span></td>
                      <td><span className={`status-badge ${submenu.status.toLowerCase()}`}>{submenu.status}</span></td>
                      <td>{submenu.menuURL}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className={`add-btn table-action-btn ${!writeAccess ? "disabled" : ""}`}
                            onClick={() => writeAccess ? handleEditSubmenu(menu, submenu) : null}
                            disabled={!writeAccess}
                            style={{ marginRight: '8px' }}
                          >
                            Edit
                          </button>
                          <span className="drag-handle">☰</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </div>
      </Container>

      {/* Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Edit {editType === 'menu' ? 'Menu' : 'Submenu'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitForm}>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <InputBox
                  type="text"
                  label='Menu Name'
                  name="menuName"
                  value={formData.menuName}
                  readOnly={true}
                />
              </Col>
              {editType === 'submenu' && (
                <Col md={12}>
                  <InputBox
                    type="text"
                    label="Submenu"
                    name="submenu"
                    value={formData.submenu}
                    readOnly={true}
                  />
                </Col>
              )}
              <Col md={12}>
                <SearchSelect
                  data={["Active", "Inactive"]}
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleInputChange}
                  setError={setError}
                  error={error}
                  submit={submit}
                  setFieldCount={setFieldCount}
                  fieldCount={fieldCount}
                  mandatoryFields={mandatoryFields}
                  setMandatoryFields={setMandatoryFields}
                />
              </Col>
              <Col md={12}>
                <InputBox
                  type="text"
                  label="Menu URL"
                  name="menuURL"
                  value={formData.menuURL}
                  onChange={handleInputChange}
                  setError={setError}
                  error={error}
                  submit={submit}
                  setFieldCount={setFieldCount}
                  fieldCount={fieldCount}
                  mandatoryFields={mandatoryFields}
                  setMandatoryFields={setMandatoryFields}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <style jsx>{`
        .menu-master-container {
          margin-top: 20px;
        }
        
        .menu-master-table {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .menu-row {
          background-color: #f8f9fa;
          font-weight: 500;
        }
        
        .submenu-row {
          background-color: white;
        }
        
        .menu-name-container {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .menu-expand-icon {
          cursor: pointer;
          user-select: none;
          color: #810055;
          font-weight: bold;
        }
        
        .submenu-name-container {
          display: flex;
          align-items: center;
          gap: 8px;
          padding-left: 20px;
        }
        
        .submenu-indent {
          color: #6c757d;
        }
        
        .action-buttons {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: bold;
        }
        
        .status-badge.active {
          background-color: #d4edda;
          color: #155724;
        }
        
        .status-badge.inactive {
          background-color: #f8d7da;
          color: #721c24;
        }
        
        .dragging {
          opacity: 0.5;
          transform: scale(1.02);
        }
        
        .drag-over {
          border-top: 3px solid #810055;
        }
      `}</style>
    </>
  );
};

export default MenuMaster;